/**
 * RobotsPanel.jsx - Spare Parts > Robots: every robot by its FANUC F-number
 * (the robot serial) with its model, configuration, customer and project.
 * Pick one for its recommended spare parts (FANUC's list): each part's
 * quantity, STANDARD and GLOBAL price, price chip and stock on hand, with the
 * whole kit's totals at list and at GLOBAL. Edit adds robots, parts to a
 * list, and imports FANUC's recommended list for the F-number (pasted from
 * Excel or a .xlsx / .csv file) through POST /api/spare-parts/import.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Modal } from '../ui/Modal';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import {
  addRobotPart,
  createRobot,
  deleteRobot,
  errorOf,
  getRobot,
  importRows,
  listRobots,
  removeRobotPart,
  updateRobot,
  updateRobotPart,
} from './sparePartsApi';
import { fmtMoney, fmtPct, parseGrid, parseText, projectLabel } from './sparePartsRules';
import { Fact, GREEN, LINE, MUTED, Msg, PriceChip, num, td, th } from './spUi';
import { Btn, Field, Grid, SelectInput, TextInput, inputStyle } from '../quotes/quoteUi';

const ROBOT_FIELDS = ['f_number', 'model', 'config_code', 'controller', 'customer', 'site', 'project_id', 'project_number', 'notes'];

export default function RobotsPanel({ editable, robotId, onPickRobot, onOpenPart, tick = 0 }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [list, setList] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | robot row
  const reads = useRef(latestReads()).current;

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const ticket = reads.start();
      const res = await listRobots({ q: query });
      if (!reads.show(ticket)) return;
      if (!res.ok) {
        if (!quiet) setMsg({ kind: 'error', text: errorOf(res, 'Could not load the robots.') });
        return;
      }
      setList(res.body.rows);
    },
    [query, reads]
  );
  useEffect(() => {
    load();
  }, [load, tick]);
  useLiveRefresh('spare-parts', () => load({ quiet: true }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <TextInput
          aria-label="Search robots"
          value={q}
          onChange={setQ}
          placeholder="Search F-number, model, configuration, customer, project..."
          style={{ maxWidth: 380 }}
        />
        {editable && (
          <Btn primary small onClick={() => setEditing('new')} style={{ marginLeft: 'auto' }}>
            + Add robot
          </Btn>
        )}
      </div>
      <Msg msg={msg} />
      {!list && !msg && <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the robots...</div>}
      {list && (
        <div
          style={{
            overflow: 'auto',
            maxHeight: robotId ? 260 : 'calc(100vh - 260px)',
            border: `1px solid ${LINE}`,
            borderRadius: 8,
            background: '#fff',
          }}
        >
          <table aria-label="Robots" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                {['F-number', 'Model', 'Configuration', 'Customer / site', 'Project', 'Parts', 'List total', 'GLOBAL total', 'Prices'].map(
                  (h, i) => (
                    <th key={h} style={{ ...th, textAlign: i >= 5 && i <= 7 ? 'right' : 'left' }}>
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const t = r.totals;
                const on = String(r.id) === String(robotId);
                return (
                  <tr
                    key={r.id}
                    onClick={() => onPickRobot(on ? null : r.id)}
                    style={{ cursor: 'pointer', background: on ? '#EEF6EA' : undefined }}
                    data-testid="robot-row"
                  >
                    <td style={{ ...td, fontWeight: 700, color: GREEN }}>{r.f_number}</td>
                    <td style={td}>{r.model || '-'}</td>
                    <td style={td}>{r.config_code || '-'}</td>
                    <td style={td}>{[r.customer, r.site].filter(Boolean).join(' · ') || '-'}</td>
                    <td style={td}>{[r.project_number, projectLabel(r)].filter(Boolean).join(' · ') || '-'}</td>
                    <td style={num}>{t.lines}</td>
                    <td style={num}>{fmtMoney(t.list_total)}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(t.global_total)}</td>
                    <td style={td}>
                      <PriceFlags t={t} />
                    </td>
                  </tr>
                );
              })}
              {!list.length && (
                <tr>
                  <td colSpan={9} style={{ ...td, textAlign: 'center', color: MUTED, padding: 24 }}>
                    {query ? 'No robots match.' : 'No robots yet. Add one by its F-number, then import its recommended spare parts.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {robotId && (
        <RobotDetail
          key={robotId}
          id={robotId}
          tick={tick}
          editable={editable}
          onOpenPart={onOpenPart}
          onEdit={setEditing}
          onGone={() => {
            onPickRobot(null);
            load();
          }}
          onChanged={() => load({ quiet: true })}
        />
      )}
      {editing && (
        <RobotEditor
          robot={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(r) => {
            setEditing(null);
            load();
            if (r) onPickRobot(r.id);
          }}
        />
      )}
    </div>
  );
}

/** "2 red · 5 unpriced" for a robot's list, or a green "All current". */
function PriceFlags({ t }) {
  const bits = [];
  if (t.stale)
    bits.push(
      <span key="r" style={{ color: '#B71C1C' }}>
        {t.stale} red
      </span>
    );
  if (t.no_price)
    bits.push(
      <span key="n" style={{ color: '#5F6368' }}>
        {t.no_price} unpriced
      </span>
    );
  if (!t.lines) return <span style={{ color: MUTED, fontSize: 11 }}>No list yet</span>;
  if (!bits.length) return <span style={{ color: GREEN, fontSize: 11, fontWeight: 700 }}>All priced</span>;
  return <span style={{ fontSize: 11, fontWeight: 700, display: 'inline-flex', gap: 6 }}>{bits}</span>;
}

function RobotDetail({ id, editable, onOpenPart, onEdit, onGone, onChanged, tick = 0 }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [adding, setAdding] = useState({ part_number: '', recommended_qty: '1' });
  const [importing, setImporting] = useState(false);
  const reads = useRef(latestReads()).current;
  // the parent hands a new function each render; the load must not follow it
  const gone = useRef(onGone);
  gone.current = onGone;
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const ticket = reads.start();
      const res = await getRobot(id);
      if (!reads.show(ticket)) return;
      if (!res.ok) {
        if (res.status === 404) {
          gone.current();
          return;
        }
        if (!quiet) setMsg({ kind: 'error', text: errorOf(res, 'Could not load the robot.') });
        return;
      }
      setData(res.body);
    },
    [id, reads]
  );
  useEffect(() => {
    load();
  }, [load, tick]);
  // a change made here re-reads the robot and the list above (its totals)
  const reload = () => {
    load();
    if (onChanged) onChanged();
  };
  useLiveRefresh('spare-parts', () => load({ quiet: true }));

  if (!data) return <div style={{ padding: 20, color: MUTED, fontSize: 12 }}>{msg ? <Msg msg={msg} /> : 'Loading the robot...'}</div>;
  const { robot, parts, totals } = data;

  const add = async () => {
    const res = await addRobotPart(id, adding);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not add the part.') });
      return;
    }
    setMsg({ kind: 'ok', text: `${res.body.part_number} is on ${robot.f_number}'s list (${res.body.recommended_qty}).` });
    setAdding({ part_number: '', recommended_qty: '1' });
    reload();
  };
  const setQty = async (line, value) => {
    const res = await updateRobotPart(id, line.line_id, { recommended_qty: value });
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not change the quantity.') });
      return;
    }
    reload();
  };
  const remove = async (line) => {
    if (!window.confirm(`Take ${line.part_number} off ${robot.f_number}'s list?`)) return;
    const res = await removeRobotPart(id, line.line_id);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not remove the part.') });
      return;
    }
    reload();
  };
  const removeRobot = async () => {
    if (!window.confirm(`Delete robot ${robot.f_number} and its recommended spare parts list? The parts stay in the catalog.`)) return;
    const res = await deleteRobot(id);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not delete the robot.') });
      return;
    }
    onGone();
  };

  return (
    <section
      aria-label={`Robot ${robot.f_number}`}
      style={{ marginTop: 14, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '14px 16px' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: GREEN }}>{robot.f_number}</div>
        <Fact label="Model">{robot.model || '-'}</Fact>
        <Fact label="Configuration">{robot.config_code || '-'}</Fact>
        <Fact label="Controller">{robot.controller || '-'}</Fact>
        <Fact label="Customer / site">{[robot.customer, robot.site].filter(Boolean).join(' · ') || '-'}</Fact>
        <Fact label="Project">{[robot.project_number, projectLabel(robot)].filter(Boolean).join(' · ') || '-'}</Fact>
        {editable && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <Btn small onClick={() => setImporting(true)}>
              Import FANUC list
            </Btn>
            <Btn small onClick={() => onEdit(robot)}>
              Edit robot
            </Btn>
            <Btn small danger onClick={removeRobot}>
              Delete
            </Btn>
          </span>
        )}
      </div>
      {robot.notes && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 6, whiteSpace: 'pre-wrap' }}>{robot.notes}</div>}
      <Msg msg={msg} />
      <div
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          margin: '12px 0',
          padding: '10px 12px',
          background: '#FAFBF7',
          border: `1px solid ${LINE}`,
          borderRadius: 8,
        }}
      >
        <Fact label="Recommended parts">
          {totals.lines} lines · {totals.qty} pcs
        </Fact>
        <Fact label="Total at list (Standard)">
          {fmtMoney(totals.list_total)}
          {totals.unpriced_standard ? <Unpriced n={totals.unpriced_standard} /> : null}
        </Fact>
        <Fact label="Total at GLOBAL">
          <b>{fmtMoney(totals.global_total)}</b>
          {totals.unpriced_global ? <Unpriced n={totals.unpriced_global} /> : null}
        </Fact>
        <Fact label="Discount (priced lines)">{fmtPct(totals.discount_pct)}</Fact>
        <Fact label="Prices">{<PriceFlags t={totals} />}</Fact>
        <Fact label="Short on the shelf">
          {totals.short} line{totals.short === 1 ? '' : 's'}
        </Fact>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table aria-label={`Recommended spare parts for ${robot.f_number}`} style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                'Part #',
                'Description',
                'Category',
                'Qty',
                'Standard',
                'GLOBAL',
                'Line at list',
                'Line at GLOBAL',
                'Price checked',
                'On hand',
                '',
              ].map((h, i) => (
                <th key={h || i} style={{ ...th, position: 'static', textAlign: [3, 4, 5, 6, 7, 9].includes(i) ? 'right' : 'left' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parts.map((p) => (
              <tr key={p.line_id}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    onClick={() => onOpenPart(p.id)}
                    style={{ all: 'unset', cursor: 'pointer', color: GREEN, fontWeight: 700 }}
                  >
                    {p.part_number}
                  </button>
                </td>
                <td style={{ ...td, maxWidth: 280 }}>{p.description || '-'}</td>
                <td style={td}>{p.category || '-'}</td>
                <td style={num}>
                  {editable ? (
                    <QtyInput value={p.recommended_qty} label={`Quantity of ${p.part_number}`} onCommit={(v) => setQty(p, v)} />
                  ) : (
                    p.recommended_qty
                  )}
                </td>
                <td style={num}>{fmtMoney(p.standard_price, p.currency)}</td>
                <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(p.global_price, p.currency)}</td>
                <td style={num}>{p.standard_price === null ? '-' : fmtMoney(p.standard_price * p.recommended_qty, p.currency)}</td>
                <td style={num}>{p.global_price === null ? '-' : fmtMoney(p.global_price * p.recommended_qty, p.currency)}</td>
                <td style={td}>
                  <PriceChip age={p.price_age} checkedAt={p.price_checked_at} compact />
                </td>
                <td
                  style={{ ...num, fontWeight: 700, color: p.on_hand >= p.recommended_qty ? GREEN : p.on_hand ? '#8D6E00' : MUTED }}
                  title={p.on_hand >= p.recommended_qty ? 'Enough on the shelf' : 'Less on the shelf than recommended'}
                >
                  {p.on_hand}
                </td>
                <td style={td}>
                  {editable && (
                    <Btn small danger aria-label={`Remove ${p.part_number}`} onClick={() => remove(p)}>
                      x
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
            {!parts.length && (
              <tr>
                <td colSpan={11} style={{ ...td, color: MUTED, textAlign: 'center', padding: 18 }}>
                  No recommended spare parts yet{editable ? " - add them below or import FANUC's list." : '.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editable && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
          <Field label="Add a catalog part">
            <TextInput
              aria-label="Part number to add"
              value={adding.part_number}
              onChange={(v) => setAdding((a) => ({ ...a, part_number: v }))}
              placeholder="A05B-2680-K001"
              style={{ width: 220 }}
            />
          </Field>
          <Field label="Qty">
            <TextInput
              aria-label="Quantity to add"
              value={adding.recommended_qty}
              onChange={(v) => setAdding((a) => ({ ...a, recommended_qty: v.replace(/\D/g, '') }))}
              style={{ width: 70 }}
            />
          </Field>
          <Btn primary small disabled={!adding.part_number.trim()} onClick={add}>
            Add to list
          </Btn>
        </div>
      )}
      {importing && (
        <ImportList
          robot={robot}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            reload();
          }}
        />
      )}
    </section>
  );
}

const Unpriced = ({ n }) => (
  <span style={{ fontSize: 10.5, color: '#8D6E00', marginLeft: 6 }}>
    ({n} line{n === 1 ? '' : 's'} unpriced)
  </span>
);

/** A quantity box that saves on Enter / leaving it, when it changed. */
function QtyInput({ value, label, onCommit }) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    if (v !== String(value) && /^\d+$/.test(v) && Number(v) > 0) onCommit(Number(v));
    else setV(String(value));
  };
  return (
    <input
      aria-label={label}
      value={v}
      onChange={(e) => setV(e.target.value.replace(/\D/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      style={{ ...inputStyle, width: 56, padding: '3px 6px', textAlign: 'right' }}
    />
  );
}

/** Add or edit a robot. */
function RobotEditor({ robot, onClose, onSaved }) {
  const { allProjects } = useApp() || {};
  const [draft, setDraft] = useState(() => Object.fromEntries(ROBOT_FIELDS.map((k) => [k, robot && robot[k] ? String(robot[k]) : ''])));
  const [msg, setMsg] = useState(null);
  const projects = useMemo(
    () =>
      (allProjects || [])
        .filter((p) => p && p.id)
        .sort((a, b) => String(b.quoteLetter || '').localeCompare(String(a.quoteLetter || ''), 'en', { numeric: true })),
    [allProjects]
  );
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    const body = {};
    for (const k of ROBOT_FIELDS) {
      const now = draft[k].trim();
      if (!robot || now !== String(robot[k] || '')) body[k] = now || null;
    }
    const res = robot ? await updateRobot(robot.id, body) : await createRobot(body);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not save the robot.') });
      return;
    }
    onSaved(res.body);
  };
  return (
    <Modal
      title={robot ? `Robot ${robot.f_number}` : 'New robot'}
      onClose={onClose}
      width={720}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary disabled={!draft.f_number.trim()} onClick={save}>
            {robot ? 'Save' : 'Add robot'}
          </Btn>
        </>
      }
    >
      <Msg msg={msg} />
      <Grid cols={3}>
        <Field label="F-number (robot serial)">
          <TextInput aria-label="F-number" value={draft.f_number} onChange={set('f_number')} placeholder="F123456" />
        </Field>
        <Field label="Model">
          <TextInput aria-label="Model" value={draft.model} onChange={set('model')} placeholder="R-2000iC/210L" />
        </Field>
        <Field label="Configuration">
          <TextInput aria-label="Configuration" value={draft.config_code} onChange={set('config_code')} placeholder="CAC210L-ULKM----" />
        </Field>
        <Field label="Controller">
          <TextInput aria-label="Controller" value={draft.controller} onChange={set('controller')} placeholder="R-30iB Plus" />
        </Field>
        <Field label="Customer">
          <TextInput aria-label="Customer" value={draft.customer} onChange={set('customer')} />
        </Field>
        <Field label="Site">
          <TextInput aria-label="Site" value={draft.site} onChange={set('site')} />
        </Field>
        {projects.length > 0 && (
          <Field label="GlobalOps project" span={2}>
            <SelectInput
              aria-label="GlobalOps project"
              value={draft.project_id}
              onChange={set('project_id')}
              blank="None"
              options={projects.map((p) => ({ value: p.id, label: projectLabel(p) || p.id }))}
            />
          </Field>
        )}
        <Field label="Project number">
          <TextInput aria-label="Project number" value={draft.project_number} onChange={set('project_number')} />
        </Field>
        <Field label="Notes" span={3}>
          <textarea
            aria-label="Robot notes"
            value={draft.notes}
            onChange={(e) => set('notes')(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>
      </Grid>
    </Modal>
  );
}

/**
 * FANUC's recommended spare parts list for this F-number: pasted from Excel
 * or read from a .xlsx / .csv file, previewed, then sent to the import. A
 * part not in the catalog yet is added with the list's description.
 */
function ImportList({ robot, onClose, onDone }) {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState({ lines: [], skipped: 0 });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setParsed(parseText(text));
  }, [text]);
  const onFile = async (file) => {
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      setParsed(parseGrid(XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' })));
      setText('');
      setMsg({ kind: 'ok', text: `Read ${file.name} (first sheet).` });
    } catch {
      setMsg({ kind: 'error', text: 'Could not read that file. Paste the rows from Excel instead.' });
    }
  };
  const send = async () => {
    setBusy(true);
    const res = await importRows({
      source: `FANUC recommended spare parts list for ${robot.f_number}`,
      robotParts: parsed.lines.map((l) => ({ ...l, f_number: robot.f_number })),
    });
    setBusy(false);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'The import failed.') });
      return;
    }
    const b = res.body;
    const errs = b.errors.length
      ? ` ${b.errors.length} line${b.errors.length === 1 ? '' : 's'} refused: ${b.errors
          .slice(0, 3)
          .map((e) => `${e.part_number || `row ${e.row}`} (${e.error})`)
          .join('; ')}`
      : '';
    setMsg({
      kind: b.errors.length ? 'error' : 'ok',
      text: `${b.lines.created} added, ${b.lines.updated} updated on the list; ${b.parts.created} new part${b.parts.created === 1 ? '' : 's'} in the catalog.${errs}`,
    });
    if (!b.errors.length) onDone();
  };
  return (
    <Modal
      title={`Import FANUC's recommended list for ${robot.f_number}`}
      onClose={onClose}
      width={760}
      footer={
        <>
          <Btn onClick={onClose}>Close</Btn>
          <Btn primary disabled={busy || !parsed.lines.length} onClick={send}>
            {busy ? 'Importing...' : `Import ${parsed.lines.length} line${parsed.lines.length === 1 ? '' : 's'}`}
          </Btn>
        </>
      }
    >
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
        Paste the rows from FANUC's list (with its header row: part number, description, quantity, category), or pick the file. Parts
        already on the list get the new quantity; parts not in the catalog are added.
      </div>
      <input
        type="file"
        accept=".xlsx,.xls,.csv"
        aria-label="FANUC list file"
        onChange={(e) => onFile(e.target.files && e.target.files[0])}
        style={{ fontSize: 12, marginBottom: 8 }}
      />
      <textarea
        aria-label="Paste the FANUC list"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder={'PART NO\tDESCRIPTION\tQTY\nA06B-6117-H209\tAMPLIFIER\t1'}
        style={{ ...inputStyle, fontFamily: 'Consolas, monospace', resize: 'vertical' }}
      />
      <Msg msg={msg} />
      {parsed.lines.length > 0 && (
        <div style={{ maxHeight: 220, overflow: 'auto', marginTop: 8, border: `1px solid ${LINE}`, borderRadius: 6 }}>
          <table aria-label="Lines to import" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Part #', 'Description', 'Qty', 'Category'].map((h) => (
                  <th key={h} style={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsed.lines.map((l, i) => (
                <tr key={`${l.part_number}-${i}`}>
                  <td style={td}>{l.part_number}</td>
                  <td style={td}>{l.description || '-'}</td>
                  <td style={num}>{l.recommended_qty}</td>
                  <td style={td}>{l.category || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {parsed.skipped > 0 && (
        <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
          {parsed.skipped} row{parsed.skipped === 1 ? '' : 's'} without a part number left out.
        </div>
      )}
    </Modal>
  );
}
